/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
module.exports = {
	functions: {
		'BIN.TO.DEC': {
			default: {
				category: 'Engineering',
				description: 'Converts a binary number to decimal.',
				inlineDescription: 'Converts a binary number to decimal.',
				arguments: [
					{
						type: 'Number',
						name: 'Number',
						description: 'The binary number to convert.',
						optional: false
					}
				],
				return: {
					type: 'Number or Error',
					description: 'The decimal number or a #NUM! [error]($HelpLink$/other/errorcodes) if given number is not a valid binary number.'
				},
				examples: {
					infoStart: '',
					infoEnd: '',
					formulas: [
						{
							formula: '=BIN.TO.DEC(1100100)',
							result: '100',
							comment: 'converts binary 1100100 to decimal'
						}
					]
				}
			}
		},
		'BIN.TO.FLOAT': {
			default: {
				category: 'Engineering',
				description: 'Converts a binary number to a floating point number. Floating point numbers are represented as a 32bit-IEEE-754 number.',
				inlineDescription: 'Converts a binary number to a floating point number. Floating point numbers are represented as a 32bit-IEEE-754 number.',
				arguments: [
					{
						type: 'Number',
						name: 'Number',
						description: 'The binary number to convert.',
						optional: false
					}
				],
				return: {
					type: 'Number or Error',
					description: 'The floating point number or a #NUM! [error]($HelpLink$/other/errorcodes) if given number is not a valid binary number.'
				},
				examples: {
					infoStart: '',
					infoEnd: '',
					formulas: [
						{
							formula: '=BIN.TO.FLOAT("01000000010010010000111111011011")',
							result: '3.1415927410125732421875',
							comment: 'converts binary number 01000000010010010000111111011011 to floating point number'
						}
					]
				}
			}
		},
		'BIN.TO.HEX': {
			default: {
				category: 'Engineering',
				description: 'Converts a binary number to hexadecimal.',
				inlineDescription: 'Converts a binary number to hexadecimal.',
				arguments: [
					{
						type: 'Number',
						name: 'Number',
						description: 'The binary number to convert.',
						optional: false
					},
					{
						type: 'Number',
						name: 'Places',
						description: 'The number of characters to use for returned value. If value has less characters then it is padded with leading zeros.',
						optional: true
					}
				],
				return: {
					type: 'Number or Error',
					description: 'The hexadecimal number or a #NUM! [error]($HelpLink$/other/errorcodes) if given number is not a valid binary number. The #VALUE! [error]($HelpLink$/other/errorcodes) is returned if specified places is not numeric or negative.'
				},
				examples: {
					infoStart: '',
					infoEnd: '',
					formulas: [
						{
							formula: '=BIN.TO.HEX(1110)',
							result: 'E',
							comment: 'converts binary 1110 to hexadecimal'
						},
						{
							formula: '=BIN.TO.HEX(1110, 4)',
							result: '000E',
							comment: 'converts binary 1110 to hexadecimal with 4 characters'
						}
					]
				}
			}
		},
		'BIN.TO.OCT': {
			default: {
				category: 'Engineering',
				description: 'Converts a binary number to octal.',
				inlineDescription: 'Converts a binary number to octal.',
				arguments: [
					{
						type: 'Number',
						name: 'Number',
						description: 'The binary number to convert.',
						optional: false
					},
					{
						type: 'Number',
						name: 'Places',
						description: 'The number of characters to use for returned value. If value has less characters then it is padded with leading zeros.',
						optional: true
					}
				],
				return: {
					type: 'Number or Error',
					description: 'The octal number or a #NUM! [error]($HelpLink$/other/errorcodes) if given number is not a valid binary number. The #VALUE! [error]($HelpLink$/other/errorcodes) is returned if specified places is not numeric or negative.'
				},
				examples: {
					infoStart: '',
					infoEnd: '',
					formulas: [
						{
							formula: '=BIN.TO.OCT(1100100)',
							result: '144',
							comment: 'converts binary 1100100 to octal'
						},
						{
							formula: '=BIN.TO.OCT(1100100, 4)',
							result: '0144',
							comment: 'converts binary 1100100 to octal with 4 characters'
						}
					]
				}
			}
		},
		'DEC.TO.BIN': {
			default: {
				category: 'Engineering',
				description: 'Converts a decimal number to binary.',
				inlineDescription: 'Converts a decimal number to binary.',
				arguments: [
					{
						type: 'Number',
						name: 'Number',
						description: 'The decimal number to convert.',
						optional: false
					},
					{
						type: 'Number',
						name: 'Places',
						description: 'The number of characters to use for returned value. If value has less characters then it is padded with leading zeros.',
						optional: true
					}
				],
				return: {
					type: 'Number or Error',
					description: 'The binary number or a #NUM! [error]($HelpLink$/other/errorcodes) if given number is not a valid decimal number. The #VALUE! [error]($HelpLink$/other/errorcodes) is returned if specified places is not numeric or negative.'
				},
				examples: {
					infoStart: '',
					infoEnd: '',
					formulas: [
						{
							formula: '=DEC.TO.BIN(9)',
							result: '1001',
							comment: 'converts decimal 9 to binary'
						},
						{
							formula: '=DEC.TO.BIN(9, 6)',
							result: '001001',
							comment: 'converts decimal 9 to binary with 6 characters'
						}
					]
				}
			}
		},
		'DEC.TO.HEX': {
			default: {
				category: 'Engineering',
				description: 'Converts a decimal number to hexadecimal.',
				inlineDescription: 'Converts a decimal number to hexadecimal.',
				arguments: [
					{
						type: 'Number',
						name: 'Number',
						description: 'The decimal number to convert.',
						optional: false
					},
					{
						type: 'Number',
						name: 'Places',
						description: 'The number of characters to use for returned value. If value has less characters then it is padded with leading zeros.',
						optional: true
					}
				],
				return: {
					type: 'Number or Error',
					description: 'The hexadecimal number or a #NUM! [error]($HelpLink$/other/errorcodes) if given number is not a valid decimal number. The #VALUE! [error]($HelpLink$/other/errorcodes) is returned if specified places is not numeric or negative.'
				},
				examples: {
					infoStart: '',
					infoEnd: '',
					formulas: [
						{
							formula: '=DEC.TO.HEX(28)',
							result: '1C',
							comment: 'converts decimal 28 to hexadecimal'
						},
						{
							formula: '=DEC.TO.HEX(28, 4)',
							result: '001C',
							comment: 'converts decimal 28 to hexadecimal with 4 characters'
						}
					]
				}
			}
		},
		'DEC.TO.OCT': {
			default: {
				category: 'Engineering',
				description: 'Converts a decimal number to octal.',
				inlineDescription: 'Converts a decimal number to octal.',
				arguments: [
					{
						type: 'Number',
						name: 'Number',
						description: 'The decimal number to convert.',
						optional: false
					},
					{
						type: 'Number',
						name: 'Places',
						description: 'The number of characters to use for returned value. If value has less characters then it is padded with leading zeros.',
						optional: true
					}
				],
				return: {
					type: 'Number or Error',
					description: 'The octal number or a #NUM! [error]($HelpLink$/other/errorcodes) if given number is not a valid decimal number. The #VALUE! [error]($HelpLink$/other/errorcodes) is returned if specified places is not numeric or negative.'
				},
				examples: {
					infoStart: '',
					infoEnd: '',
					formulas: [
						{
							formula: '=DEC.TO.OCT(58)',
							result: '72',
							comment: 'converts decimal 58 to octal'
						},
						{
							formula: '=DEC.TO.OCT(58, 4)',
							result: '0072',
							comment: 'converts decimal 58 to octal with 4 characters'
						}
					]
				}
			}
		},
		'FLOAT.TO.BIN': {
			default: {
				category: 'Engineering',
				description: 'Converts a floating point number to binary. Floating point numbers are represented as a 32bit-IEEE-754 number.',
				inlineDescription: 'Converts a floating point number to binary. Floating point numbers are represented as a 32bit-IEEE-754 number.',
				arguments: [
					{
						type: 'Number',
						name: 'Number',
						description: 'The floating point number to convert.',
						optional: false
					}
				],
				return: {
					type: 'Number or Error',
					description: 'The binary number or a #NUM! [error]($HelpLink$/other/errorcodes) if given number is not a valid floating point number.'
				},
				examples: {
					infoStart: '',
					infoEnd: '',
					formulas: [
						{
							formula: '=FLOAT.TO.BIN(2.567)',
							result: '01000000001001000100100110111010',
							comment: 'converts floating point 2.567 to binary'
						}
					]
				}
			}
		},
		'FLOAT.TO.HEX': {
			default: {
				category: 'Engineering',
				description: 'Converts a floating point number to hexadecimal. Floating point numbers are represented as a 32bit-IEEE-754 number.',
				inlineDescription: 'Converts a floating point number to hexadecimal. Floating point numbers are represented as a 32bit-IEEE-754 number.',
				arguments: [
					{
						type: 'Number',
						name: 'Number',
						description: 'The floating point number to convert.',
						optional: false
					}
				],
				return: {
					type: 'Number or Error',
					description: 'The hexadecimal number or a #NUM! [error]($HelpLink$/other/errorcodes) if given number is not a valid floating point number.'
				},
				examples: {
					infoStart: '',
					infoEnd: '',
					formulas: [
						{
							formula: '=FLOAT.TO.HEX(2.567)',
							result: '402449BA',
							comment: 'converts floating point 2.567 to hexadecimal'
						}
					]
				}
			}
		},
		'HEX.TO.BIN': {
			default: {
				category: 'Engineering',
				description: 'Converts a hexadecimal number to binary.',
				inlineDescription: 'Converts a hexadecimal number to binary.',
				arguments: [
					{
						type: 'Number',
						name: 'Number',
						description: 'The hexadecimal number to convert.',
						optional: false
					},
					{
						type: 'Number or Error',
						name: 'Places',
						description: 'The number of characters to use for returned value. If value has less characters then it is padded with leading zeros.',
						optional: true
					}
				],
				return: {
					type: '',
					description: 'The binary number or a #NUM! [error]($HelpLink$/other/errorcodes) if given number is not a valid hexadecimal number. The #VALUE! [error]($HelpLink$/other/errorcodes) is returned if specified places is not numeric or negative.'
				},
				examples: {
					infoStart: '',
					infoEnd: '',
					formulas: [
						{
							formula: '=HEX.TO.BIN("F")',
							result: '1111',
							comment: 'converts hexadecimal F to binary'
						},
						{
							formula: '=HEX.TO.BIN("F", 6)',
							result: '001111',
							comment: 'converts hexadecimal F to binary with 6 characters'
						}
					]
				}
			}
		},
		'HEX.TO.DEC': {
			default: {
				category: 'Engineering',
				description: 'Converts a hexadecimal number to decimal.',
				inlineDescription: 'Converts a hexadecimal number to decimal.',
				arguments: [
					{
						type: 'Number',
						name: 'Number',
						description: 'The hexadecimal number to convert.',
						optional: false
					}
				],
				return: {
					type: 'Number or Error',
					description: 'The decimal number or a #NUM! [error]($HelpLink$/other/errorcodes) if given number is not a valid hexadecimal number.'
				},
				examples: {
					infoStart: '',
					infoEnd: '',
					formulas: [
						{
							formula: '=HEX.TO.DEC("A5")',
							result: '165',
							comment: 'converts hexadecimal A5 to decimal'
						}
					]
				}
			}
		},
		'HEX.TO.FLOAT': {
			default: {
				category: 'Engineering',
				description: 'Converts a hexadecimal number to a floating point number. Floating point numbers are represented as a 32bit-IEEE-754 number.',
				inlineDescription: 'Converts a hexadecimal number to a floating point number. Floating point numbers are represented as a 32bit-IEEE-754 number.',
				arguments: [
					{
						type: 'Number',
						name: 'Number',
						description: 'The hexadecimal number to convert.',
						optional: false
					}
				],
				return: {
					type: 'Number or Error',
					description: 'The floating point number or a #NUM! [error]($HelpLink$/other/errorcodes) if given number is not a valid hexadecimal number.'
				},
				examples: {
					infoStart: '',
					infoEnd: '',
					formulas: [
						{
							formula: '=HEX.TO.FLOAT("40490FDB")',
							result: '3.1415927410125732421875',
							comment: 'converts hexadecimal number 40490FDB to floating point number'
						}
					]
				}
			}
		},
		'HEX.TO.OCT': {
			default: {
				category: 'Engineering',
				description: 'Converts a hexadecimal number to octal.',
				inlineDescription: 'Converts a hexadecimal number to octal.',
				arguments: [
					{
						type: 'Number',
						name: 'Number',
						description: 'The hexadecimal number to convert.',
						optional: false
					},
					{
						type: 'Number',
						name: 'Places',
						description: 'The number of characters to use for returned value. If value has less characters then it is padded with leading zeros.',
						optional: true
					}
				],
				return: {
					type: 'Number or Error',
					description: 'The octal number or a #NUM! [error]($HelpLink$/other/errorcodes) if given number is not a valid hexadecimal number. The #VALUE! [error]($HelpLink$/other/errorcodes) is returned if specified places is not numeric or negative.'
				},
				examples: {
					infoStart: '',
					infoEnd: '',
					formulas: [
						{
							formula: '=HEX.TO.OCT("F")',
							result: '17',
							comment: 'converts hexadecimal F to octal'
						},
						{
							formula: '=HEX.TO.OCT("F", 4)',
							result: '0017',
							comment: 'converts hexadecimal F to octal with 4 characters'
						}
					]
				}
			}
		},
		'OCT.TO.BIN': {
			default: {
				category: 'Engineering',
				description: 'Converts an octal number to binary.',
				inlineDescription: 'Converts an octal number to binary.',
				arguments: [
					{
						type: 'Number',
						name: 'Number',
						description: 'The octal number to convert.',
						optional: false
					},
					{
						type: 'Number',
						name: 'Places',
						description: 'The number of characters to use for returned value. If value has less characters then it is padded with leading zeros.',
						optional: true
					}
				],
				return: {
					type: 'Number or Error',
					description: 'The binary number or a #NUM! [error]($HelpLink$/other/errorcodes) if given number is not a valid octal number. The #VALUE! [error]($HelpLink$/other/errorcodes) is returned if specified places is not numeric or negative.'
				},
				examples: {
					infoStart: '',
					infoEnd: '',
					formulas: [
						{
							formula: '=OCT.TO.BIN(3)',
							result: '11',
							comment: 'converts octal 3 to binary'
						},
						{
							formula: '=OCT.TO.BIN(3, 4)',
							result: '0011',
							comment: 'converts octal 3 to binary with 4 characters'
						}
					]
				}
			}
		},
		'OCT.TO.DEC': {
			default: {
				category: 'Engineering',
				description: 'Converts an octal number to decimal.',
				inlineDescription: 'Converts an octal number to decimal.',
				arguments: [
					{
						type: 'Number',
						name: 'Number',
						description: 'The octal number to convert.',
						optional: false
					}
				],
				return: {
					type: 'Number or Error',
					description: 'The decimal number or a #NUM! [error]($HelpLink$/other/errorcodes) if given number is not a valid octal number.'
				},
				examples: {
					infoStart: '',
					infoEnd: '',
					formulas: [
						{
							formula: '=OCT.TO.DEC(54)',
							result: '44',
							comment: 'converts octal 54 to decimal'
						}
					]
				}
			}
		},
		'OCT.TO.HEX': {
			default: {
				category: 'Engineering',
				description: 'Converts an octal number to hexadecimal.',
				inlineDescription: 'Converts an octal number to hexadecimal.',
				arguments: [
					{
						type: 'Number',
						name: 'Number',
						description: 'The octal number to convert.',
						optional: false
					},
					{
						type: 'Number',
						name: 'Places',
						description: 'The number of characters to use for returned value. If value has less characters then it is padded with leading zeros.',
						optional: true
					}
				],
				return: {
					type: 'Number or Error',
					description: 'The hexadecimal number or a #NUM! [error]($HelpLink$/other/errorcodes) if given number is not a valid octal number. The #VALUE! [error]($HelpLink$/other/errorcodes) is returned if specified places is not numeric or negative.'
				},
				examples: {
					infoStart: '',
					infoEnd: '',
					formulas: [
						{
							formula: '=OCT.TO.HEX(100)',
							result: '40',
							comment: 'converts octal 100 to hexadecimal'
						},
						{
							formula: '=OCT.TO.HEX(100, 4)',
							result: '0040',
							comment: 'converts octal 100 to hexadecimal with 4 characters'
						}
					]
				}
			}
		}
	},
};
